import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { BookMarked, Building2, Plus, Search, Share2, Download, Trash2, Pencil, Star, Users, Loader2, Sparkles, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const EMPTY = {
  name: "", address: "", phone: "", place_type: "Autre", specialite: "",
  praticien: "", floor: "", access_code: "", hours: "", notes: "",
  lat: null, lon: null,
};

export default function Annuaire() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ types: [], specialites: [] });
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [placeType, setPlaceType] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      params.set("scope", scope);
      if (placeType) params.set("place_type", placeType);
      if (specialite) params.set("specialite", specialite);
      const { data } = await api.get(`/places?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/places/meta").then(r => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [q, scope, placeType, specialite]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setEditing(null); setForm(EMPTY); };
  const save = async () => {
    if (!form.name.trim() || !form.address.trim()) { toast.error("Nom et adresse requis"); return; }
    try {
      if (editing) {
        await api.put(`/places/${editing.id}`, form);
        toast.success("Lieu mis à jour");
      } else {
        await api.post("/places", form);
        toast.success("Lieu ajouté à votre annuaire");
      }
      closeDialog(); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    try { await api.delete(`/places/${p.id}`); toast.success("Lieu supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const publish = async (p) => {
    try { await api.post(`/places/${p.id}/publish`); toast.success("Publié dans la base commune"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unpublish = async (p) => {
    try { await api.post(`/places/${p.id}/unpublish`); toast.success("Retiré de la base commune"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const importPublic = async (p) => {
    try {
      await api.post(`/places/${p.id}/import`);
      toast.success("Lieu ajouté à votre annuaire");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const counters = useMemo(() => {
    const mine = items.filter(i => i.mine).length;
    const publics = items.filter(i => i.is_public && !i.mine).length;
    return { mine, publics, total: items.length };
  }, [items]);

  return (
    <div className="p-6 space-y-6" data-testid="annuaire-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><BookMarked className="h-6 w-6 text-primary" /> Annuaire médical</h1>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            Vos lieux de rendez-vous récurrents (CH, cabinets, laboratoires, EHPAD, dialyses…) — fiche enrichie
            (téléphone, étage, code d'accès, horaires, remarques). Chaque adresse utilisée dans une course est
            automatiquement mémorisée. Publiez vos meilleurs contacts dans la <strong>base commune</strong> pour
            les partager avec les autres gérants PACEMIA.
          </p>
        </div>
        <Button onClick={openNew} data-testid="annuaire-new"><Plus className="h-4 w-4 mr-1.5" /> Ajouter un lieu</Button>
      </div>

      <Tabs defaultValue="carnet" className="w-full" data-testid="annuaire-tabs">
        <TabsList data-testid="annuaire-tabs-list">
          <TabsTrigger value="carnet" data-testid="annuaire-tab-carnet"><BookMarked className="h-3.5 w-3.5 mr-1.5" /> Mon carnet</TabsTrigger>
          <TabsTrigger value="suggestions" data-testid="annuaire-tab-suggestions"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Suggestions du secteur</TabsTrigger>
        </TabsList>

        <TabsContent value="carnet" className="space-y-4 mt-4">

      <Card className="p-4 rounded-md shadow-none">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Recherche</Label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" />
              <Input className="pl-8" placeholder="Nom, adresse, praticien…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="annuaire-q" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Visibilité</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-44 mt-1" data-testid="annuaire-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous (mien + commun)</SelectItem>
                <SelectItem value="mine">Mon annuaire</SelectItem>
                <SelectItem value="public">Base commune</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={placeType || "__all__"} onValueChange={(v) => setPlaceType(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-52 mt-1" data-testid="annuaire-type"><SelectValue placeholder="Tous types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous types</SelectItem>
                {(meta.types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Spécialité</Label>
            <Select value={specialite || "__all__"} onValueChange={(v) => setSpecialite(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-52 mt-1" data-testid="annuaire-spec"><SelectValue placeholder="Toutes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes</SelectItem>
                {(meta.specialites || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-3 flex items-center gap-4">
          <span>{counters.total} résultat(s)</span>
          <span className="flex items-center gap-1"><BookMarked className="h-3 w-3 text-primary" /> {counters.mine} à moi</span>
          <span className="flex items-center gap-1"><Users className="h-3 w-3 text-amber-500" /> {counters.publics} publics</span>
        </div>
      </Card>

      {loading && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="annuaire-grid">
        {items.map((p) => (
          <Card key={p.id} className="p-4 rounded-md shadow-none flex flex-col gap-2" data-testid={`annuaire-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {p.mine
                    ? <BookMarked className="h-4 w-4 text-primary shrink-0" title="Mon annuaire" />
                    : <Star className="h-4 w-4 text-amber-500 shrink-0" title="Base commune" />}
                  <div className="font-medium truncate">{p.name}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Building2 className="h-3 w-3" />{p.place_type}{p.specialite ? ` · ${p.specialite}` : ""}</div>
              </div>
              {p.is_public && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 border border-amber-300">Publié</span>
              )}
            </div>
            <div className="text-sm text-muted-foreground truncate" title={p.address}>{p.address}</div>
            <div className="text-xs text-muted-foreground grid grid-cols-2 gap-x-3 gap-y-0.5">
              {p.phone && <span>📞 {p.phone}</span>}
              {p.floor && <span>🏢 {p.floor}</span>}
              {p.access_code && <span>🔑 {p.access_code}</span>}
              {p.hours && <span className="col-span-2 truncate" title={p.hours}>🕒 {p.hours}</span>}
              {p.praticien && <span className="col-span-2 truncate" title={p.praticien}>👤 {p.praticien}</span>}
            </div>
            {p.notes && <div className="text-xs italic text-muted-foreground line-clamp-2">{p.notes}</div>}
            <div className="text-[11px] text-muted-foreground pt-1">Utilisé {p.uses_count || 0} fois</div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {p.mine ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`annuaire-edit-${p.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Modifier</Button>
                  {!p.is_public
                    ? <Button size="sm" variant="outline" onClick={() => publish(p)} data-testid={`annuaire-publish-${p.id}`}><Share2 className="h-3.5 w-3.5 mr-1" />Publier</Button>
                    : <Button size="sm" variant="ghost" onClick={() => unpublish(p)} data-testid={`annuaire-unpublish-${p.id}`}>Retirer du commun</Button>}
                  <Button size="sm" variant="ghost" onClick={() => del(p)} className="text-destructive" data-testid={`annuaire-del-${p.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Suppr.</Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => importPublic(p)} data-testid={`annuaire-import-${p.id}`}><Download className="h-3.5 w-3.5 mr-1" />Ajouter à mon annuaire</Button>
              )}
            </div>
          </Card>
        ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-sm text-muted-foreground border border-dashed rounded-md p-8 text-center">
            Aucun lieu ne correspond à votre recherche. Créez-en un ou consultez la base commune.
          </div>
        )}
      </div>

        </TabsContent>

        <TabsContent value="suggestions" className="space-y-4 mt-4">
          <SuggestionsPanel onImported={load} />
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) closeDialog(); }}>
        <DialogContent className="max-w-2xl" data-testid="annuaire-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le lieu" : "Nouveau lieu"}</DialogTitle>
            <DialogDescription>Une fiche enrichie facilite la vie des équipes en course.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Nom du lieu *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="CH Dijon Bocage - Entrée Urgences" data-testid="place-name" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Adresse *</Label>
              <div className="mt-1">
                <AddressAutocomplete
                  value={form.address}
                  onChange={(txt) => setForm({ ...form, address: txt })}
                  onSelect={(r) => {
                    // Si la suggestion vient de l'annuaire (mien ou base commune),
                    // pré-remplit toutes les infos disponibles pour gagner du temps.
                    const p = r.place || {};
                    setForm({
                      ...form,
                      address: r.label || r.address || form.address,
                      lat: r.lat, lon: r.lon,
                      name: form.name || p.name || (r.label || "").split(",")[0] || "",
                      place_type: p.place_type || form.place_type,
                      specialite: p.specialite || form.specialite,
                      phone: p.phone || form.phone,
                      floor: p.floor || form.floor,
                      notes: p.notes || form.notes,
                    });
                  }}
                  placeholder="Nom, adresse ou lieu déjà connu"
                  testid="place-address"
                  excludeType="Domicile patient"
                />
              </div>
              {form.lat && form.lon && (
                <p className="text-[10px] text-muted-foreground mt-1">📍 {Number(form.lat).toFixed(4)}, {Number(form.lon).toFixed(4)} (GPS calculé)</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.place_type} onValueChange={(v) => setForm({ ...form, place_type: v })}>
                <SelectTrigger data-testid="place-type"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta.types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Spécialité</Label>
              <Select value={form.specialite || "__none__"} onValueChange={(v) => setForm({ ...form, specialite: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="place-spec"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {(meta.specialites || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="03 80 XX XX XX" data-testid="place-phone" />
            </div>
            <div>
              <Label className="text-xs">Praticien référent</Label>
              <Input value={form.praticien} onChange={(e) => setForm({ ...form, praticien: e.target.value })} placeholder="Dr Martin" data-testid="place-praticien" />
            </div>
            <div>
              <Label className="text-xs">Étage / Bâtiment / Service</Label>
              <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder="Bât. B - 3ᵉ étage - Dialyse" data-testid="place-floor" />
            </div>
            <div>
              <Label className="text-xs">Code d'accès</Label>
              <Input value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value })} placeholder="A1234B" data-testid="place-code" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Horaires d'ouverture</Label>
              <Input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="Lun-Ven 8h-19h · Sam 8h-13h · Fermé le dim." data-testid="place-hours" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Remarques</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Se garer sur le parking dépose-minute. Sonner à l'interphone Dialyse." data-testid="place-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog} data-testid="place-cancel">Annuler</Button>
            <Button onClick={save} data-testid="place-save">{editing ? "Enregistrer" : "Ajouter"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SuggestionsPanel({ onImported }) {
  const [items, setItems] = useState([]);
  const [radius, setRadius] = useState(30);
  const [loading, setLoading] = useState(false);
  const [center, setCenter] = useState(null);
  const [importing, setImporting] = useState(null);

  const load = async (r = radius) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/places/suggestions?radius_km=${r}&limit=60`);
      setItems(data.items || []);
      setCenter(data.center);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(radius); /* eslint-disable-next-line */ }, [radius]);

  const doImport = async (p) => {
    setImporting(p.id);
    try {
      await api.post(`/places/${p.id}/import`);
      toast.success("Ajouté à votre annuaire");
      setItems(items.filter(i => i.id !== p.id));
      onImported?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setImporting(null); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-md shadow-none flex items-center gap-4 flex-wrap" data-testid="suggestions-header">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-muted-foreground">Lieux publiés par d'autres gérants autour de <strong>votre société</strong>{center ? ` (${center.lat.toFixed(3)}, ${center.lon.toFixed(3)})` : ""}.</span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Label className="text-xs">Rayon</Label>
          <Select value={String(radius)} onValueChange={(v) => setRadius(Number(v))}>
            <SelectTrigger className="w-28" data-testid="suggestions-radius"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[10, 20, 30, 50, 80, 120].map(r => <SelectItem key={r} value={String(r)}>{r} km</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="suggestions-grid">
        {items.map((p) => (
          <Card key={p.id} className="p-4 rounded-md shadow-none flex flex-col gap-2" data-testid={`suggestion-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-amber-500 shrink-0" />
                  <div className="font-medium truncate">{p.name}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Building2 className="h-3 w-3" />{p.place_type}{p.specialite ? ` · ${p.specialite}` : ""}</div>
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30 flex items-center gap-0.5" title="Distance à vol d'oiseau depuis votre société">
                <MapPin className="h-2.5 w-2.5" />{p.distance_km} km
              </span>
            </div>
            <div className="text-sm text-muted-foreground truncate" title={p.address}>{p.address}</div>
            <div className="text-xs text-muted-foreground grid grid-cols-2 gap-x-3 gap-y-0.5">
              {p.phone && <span>📞 {p.phone}</span>}
              {p.floor && <span>🏢 {p.floor}</span>}
              {p.hours && <span className="col-span-2 truncate" title={p.hours}>🕒 {p.hours}</span>}
              {p.praticien && <span className="col-span-2 truncate" title={p.praticien}>👤 {p.praticien}</span>}
            </div>
            {p.notes && <div className="text-xs italic text-muted-foreground line-clamp-2">{p.notes}</div>}
            <div className="text-[11px] text-muted-foreground pt-1">Contribué · utilisé {p.uses_count || 0} fois par d'autres</div>
            <div className="pt-1">
              <Button size="sm" variant="outline" onClick={() => doImport(p)} disabled={importing === p.id} data-testid={`suggestion-import-${p.id}`}>
                {importing === p.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                Ajouter à mon annuaire
              </Button>
            </div>
          </Card>
        ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-sm text-muted-foreground border border-dashed rounded-md p-8 text-center">
            Aucune suggestion à moins de {radius} km — élargissez le rayon ou revenez plus tard lorsque d'autres gérants auront publié des lieux dans votre secteur.
          </div>
        )}
      </div>
    </div>
  );
}
